import { IFetchMixingCycleParams, IMixingCycleSettings } from '@/interfaces/api/mixing-cycle'
import {
  IAlarmBurntResistorSettings,
  IAlarmDefrostTimeSettings,
  IAlarmDoorOpenSettings,
  IAlarmProfileSettings,
  IAlarmLowLoadSettings,
  IAlarmNoDataSettings,
  IAlarmPeakHourSettings,
  IAlarmTemperatureSettings,
  IAlarmEmptyMixerSettings,
  IAlarmPowerFactorSettings,
  IBurntResistorAlarmParams,
  IDefrostTimeAlarmParams,
  IDoorOpenAlarmParams,
  IInterruptionAlarmParams,
  ILowLoadAlarmParams,
  INoDataAlarmParams,
  IPeakHourAlarmParams,
  ITemperatureAlarmParams,
  IPowerFactorAlarmParams,
  IEmptyMixerAlarmParams,
  IOutsideRegularTimeParams,
  IAlarmOutsideRegularTimeSettings,
  IOvervoltageAlarmParams,
  ICreateSensorParams
} from '@/interfaces/api/sensor'
import type {
  IDailyHourlyReadingsParams,
  IDailyHourlyReadingsResponse,
  IDailyTemperatureReportParams,
  IDailyTemperatureReportResponse,
} from '@/interfaces/entities/daily-temperature'
import ApiService from '../api.service'
import getCurrentCompanyId from '../company.service'

const SensorResource = {
  getAllSensors: () => ApiService.get(`/v1/companies/${getCurrentCompanyId()}/sensors/all`),
  getSensorsByCompanyId: (companyId: number) => ApiService.get(`/v1/companies/${companyId}/sensors/all`),

  getSensorsIndexedByCompany: (targetCompanyId: number) =>
    ApiService.get(`/v1/companies/${getCurrentCompanyId()}/sensors/index_sensors_by_company`, {
      target_company_id: targetCompanyId,
    }),
  getEnergySensors: () => ApiService.get(`/v1/companies/${getCurrentCompanyId()}/sensors/energy_sensors`),

  // Temperatura Diária: leituras horárias de UM dia. Filtros opcionais:
  // shift_id, start_hour + end_hour, sensor_ids (CSV), page, items.
  getDailyHourlyTemperature: (params: IDailyHourlyReadingsParams) => {
    const { sensor_ids, ...rest } = params
    const query: Record<string, string | number | boolean> = { ...rest }
    if (sensor_ids?.length) {
      query.sensor_ids = sensor_ids.join(',')
    }
    return ApiService.get(
      `/v1/companies/${getCurrentCompanyId()}/sensors/temperature_sensors/daily_hourly_readings`,
      query,
    ) as Promise<IDailyHourlyReadingsResponse>
  },

  // Solicita o PDF de N dias corridos (assíncrono) — enviado por e-mail.
  requestDailyTemperatureReport: (params: IDailyTemperatureReportParams) => {
    const { sensor_ids, ...rest } = params
    const body: Record<string, string | number | boolean | undefined> = { ...rest }
    if (sensor_ids?.length) {
      body.sensor_ids = sensor_ids.join(',')
    }
    return ApiService.post(
      `/v1/companies/${getCurrentCompanyId()}/sensors/temperature_sensors/daily_hourly_report`,
      body,
    ) as Promise<IDailyTemperatureReportResponse>
  },

  getEnergySensorsById: (sensorId: number) => ApiService.get(`/v1/companies/${getCurrentCompanyId()}/sensors/energy_sensors/${sensorId}`),
  /** Último registro no Influx (corrente, tensão, FP). Sem parâmetros — retorna sempre o último. */
  getInfluxEnergyMetrics: (sensorId: number) =>
    ApiService.get(
      `/v1/companies/${getCurrentCompanyId()}/sensors/energy_sensors/${sensorId}/influx_energy_metrics`,
    ) as Promise<{
      last_reading_at?: string | null
      readings?: {
        current_a?: number | null
        current_b?: number | null
        current_c?: number | null
        voltage_a?: number | null
        voltage_b?: number | null
        voltage_c?: number | null
        power_factor?: number | null
        power_factor_a?: number | null
        power_factor_b?: number | null
        power_factor_c?: number | null
      }
    }>,
  getOpenSensorsById: (sensorId: number) => ApiService.get(`/v1/companies/${getCurrentCompanyId()}/sensors/open_sensors/${sensorId}`),
  getTemperatureSensorsById: (sensorId: number) => ApiService.get(`/v1/companies/${getCurrentCompanyId()}/sensors/temperature_sensors/${sensorId}`),

  /** Amostra do último payload MQTT recebido e configuração do schema (para debug de erro invalid_payload) */
  getMqttPayloadSample: (sensorId: number) =>
    ApiService.get(`/v1/companies/${getCurrentCompanyId()}/sensors/${sensorId}/mqtt_payload_sample`) as Promise<{
      raw_message?: string | null
      parsed_payload?: Record<string, unknown> | null
      error_code?: string | null
      error_message?: string | null
      received_at?: string | null
      schema_config?: Record<string, string> | null
      expected_data_keys?: string[]
      expected_format_description?: string
    }>,

  activeInterruptionAlarm: (sensorId: number, params: IInterruptionAlarmParams) =>
    ApiService.post(`/v1/companies/${getCurrentCompanyId()}/sensors/energy_sensors/${sensorId}/activate_interruption_alarm`, params),
  activateLowLoadAlarm: (sensorId: number, params: ILowLoadAlarmParams) =>
    ApiService.post(`/v1/companies/${getCurrentCompanyId()}/sensors/energy_sensors/${sensorId}/activate_low_load_alarm`, params),
  activateDefrostTimeAlarm: (sensorId: number, params: IDefrostTimeAlarmParams) =>
    ApiService.post(`/v1/companies/${getCurrentCompanyId()}/sensors/energy_sensors/${sensorId}/activate_defrost_time_alarm`, params),
  activateBurntResistorAlarm: (sensorId: number, params: IBurntResistorAlarmParams) =>
    ApiService.post(`/v1/companies/${getCurrentCompanyId()}/sensors/energy_sensors/${sensorId}/activate_burnt_resistor_alarm`, params),
  activateNoDataAlarm: (sensorId: number, sensorTypePath: string, params: INoDataAlarmParams) =>
    ApiService.post(`/v1/companies/${getCurrentCompanyId()}/sensors/${sensorTypePath}/${sensorId}/activate_no_data_alarm`, params),
  activateOvervoltageAlarm: (sensorId: number, params: IOvervoltageAlarmParams) =>
    ApiService.post(`/v1/companies/${getCurrentCompanyId()}/sensors/energy_sensors/${sensorId}/activate_overvoltage_alarm`, params),
  activateEmptyMixerAlarm: (sensorId: number, params: IEmptyMixerAlarmParams) =>
    ApiService.post(`/v1/companies/${getCurrentCompanyId()}/sensors/energy_sensors/${sensorId}/activate_empty_mixer_alarm`, params),
  activateOutsideRegularTimeAlarm: (sensorId: number, params: IOutsideRegularTimeParams) =>
    ApiService.post(`/v1/companies/${getCurrentCompanyId()}/sensors/energy_sensors/${sensorId}/activate_outside_regular_time_alarm`, params),
  alarmNoDataSettings: (sensorId: number, sensorTypePath: string, params: IAlarmNoDataSettings) =>
    ApiService.put(`/v1/companies/${getCurrentCompanyId()}/sensors/${sensorTypePath}/${sensorId}/alarms_settings`, params),
  activatePowerFactorAlarm: (sensorId: number, params: IPowerFactorAlarmParams) =>
    ApiService.post(`/v1/companies/${getCurrentCompanyId()}/sensors/energy_sensors/${sensorId}/activate_power_factor_alarm`, params),
  alarmPowerFactorSettings: (sensorId: number, params: IAlarmPowerFactorSettings) =>
    ApiService.put(`/v1/companies/${getCurrentCompanyId()}/sensors/energy_sensors/${sensorId}/alarms_settings`, params),
  alarmTemperatureSettings: (sensorId: number, params: IAlarmTemperatureSettings) =>
    ApiService.put(`/v1/companies/${getCurrentCompanyId()}/sensors/temperature_sensors/${sensorId}/alarms_settings`, params),

  alarmLowLoadSettings: (sensorId: number, params: IAlarmLowLoadSettings) =>
    ApiService.put(`/v1/companies/${getCurrentCompanyId()}/sensors/energy_sensors/${sensorId}/alarms_settings`, params),
  alarmDefrostTimeSettings: (sensorId: number, params: IAlarmDefrostTimeSettings) =>
    ApiService.put(`/v1/companies/${getCurrentCompanyId()}/sensors/energy_sensors/${sensorId}/alarms_settings`, params),
  alarmBurntResistorSettings: (sensorId: number, params: IAlarmBurntResistorSettings) =>
    ApiService.put(`/v1/companies/${getCurrentCompanyId()}/sensors/energy_sensors/${sensorId}/alarms_settings`, params),
  alarmEmptyMixerSettings: (sensorId: number, params: IAlarmEmptyMixerSettings) =>
    ApiService.put(`/v1/companies/${getCurrentCompanyId()}/sensors/energy_sensors/${sensorId}/alarms_settings`, params),
  alarmOutsideRegularTimeSettings: (sensorId: number, params: IAlarmOutsideRegularTimeSettings) =>
    ApiService.put(`/v1/companies/${getCurrentCompanyId()}/sensors/energy_sensors/${sensorId}/alarms_settings`, params),

  activatePeakHourAlarm: (sensorId: number, params: IPeakHourAlarmParams) =>
    ApiService.post(`/v1/companies/${getCurrentCompanyId()}/sensors/energy_sensors/${sensorId}/activate_peak_hour_activations_alarm`, params),
  alarmPeakHourSettings: (sensorId: number, params: IAlarmPeakHourSettings) =>
    ApiService.put(`/v1/companies/${getCurrentCompanyId()}/sensors/energy_sensors/${sensorId}/alarms_settings`, params),
  activateTemperatureAlarm: (sensorId: number, params: ITemperatureAlarmParams) =>
    ApiService.post(`/v1/companies/${getCurrentCompanyId()}/sensors/temperature_sensors/${sensorId}/activate_critical_temperature_alarm`, params),

  alarmProfileSettings: (sensorId: number, params: IAlarmProfileSettings) =>
    ApiService.put(`/v1/companies/${getCurrentCompanyId()}/sensors/energy_sensors/${sensorId}/alarms_settings`, params),

  alarmProfileTemperatureSettings: (sensorId: number, params: IAlarmProfileSettings) =>
    ApiService.put(`/v1/companies/${getCurrentCompanyId()}/sensors/temperature_sensors/${sensorId}/alarms_settings`, params),

  alarmDoorOpenSettings: (sensorId: number, params: IAlarmProfileSettings) =>
    ApiService.put(`/v1/companies/${getCurrentCompanyId()}/sensors/open_sensors/${sensorId}/alarms_settings`, params),


  getEnergySensorReadings: ({
    sensorId,
    dateRange,
    resolution,
    aggregation,
  }: {
    sensorId: number
    dateRange: string
    resolution: string
    aggregation: string
  }) =>
    ApiService.get(
      `/v1/companies/${getCurrentCompanyId()}/sensors/energy_sensors/${sensorId}/readings?${dateRange}&window_period=${resolution}&aggregation_function=${aggregation}`,
    ),

  /** Histórico de tensão e fator de potência (séries dinâmicas + fixed_values do schema).
   * Chaves possíveis em `readings` / `fixed_values`:
   * `voltage_a|b|c`, `power_factor_a|b|c`, `power_factor` (total).
   * Dinâmico → série em readings; fixo → constante em fixed_values (nunca nos dois).
   */
  getEnergyVoltagePowerFactorReadings: ({
    sensorId,
    dateRange,
    resolution,
    aggregation,
  }: {
    sensorId: number
    dateRange: string
    resolution: string
    aggregation: string
  }) =>
    ApiService.get(
      `/v1/companies/${getCurrentCompanyId()}/sensors/energy_sensors/${sensorId}/voltage_power_factor_readings?${dateRange}&window_period=${resolution}&aggregation_function=${aggregation}`,
    ) as Promise<{
      sensor?: Record<string, unknown>
      start_date?: string
      end_date?: string
      readings?: Record<string, Array<{ t?: string; v?: number | string }>>
      fixed_values?: Record<string, number>
    }>,

  /** Último registro no Influx (door_open). Sem parâmetros — retorna sempre o último. */
  getOpenInfluxLastReading: (sensorId: number) =>
    ApiService.get(
      `/v1/companies/${getCurrentCompanyId()}/sensors/open_sensors/${sensorId}/influx_last_reading`,
    ) as Promise<{
      last_reading_at?: string | null
      readings?: Record<string, Array<{ t?: string; v?: number }>>
    }>,

  /** Converte resolução do modal (ex: "30s", "1m") para segundos para a API de door open. */
  resolutionToSeconds: (resolution: string): number => {
    if (resolution.endsWith('s')) return parseInt(resolution, 10) || 30
    if (resolution.endsWith('m')) return (parseInt(resolution, 10) || 1) * 60
    return 30
  },

  getOpenSensorReadings: ({
    sensorId,
    dateRange,
    resolution = '30s',
  }: {
    sensorId: number
    dateRange: string
    resolution?: string
  }) => {
    const windowPeriod = SensorResource.resolutionToSeconds(resolution)
    return ApiService.get(
      `/v1/companies/${getCurrentCompanyId()}/sensors/open_sensors/${sensorId}/readings?${dateRange}&window_period=${windowPeriod}`,
    )
  },
  /** Endpoint otimizado: retorna apenas o último registro no Influx (usa last()). Para polling em tempo real. */
  getTemperatureInfluxLastReading: (sensorId: number) =>
    ApiService.get(
      `/v1/companies/${getCurrentCompanyId()}/sensors/temperature_sensors/${sensorId}/influx_last_reading`,
    ) as Promise<{
      last_reading_at?: string | null
      readings?: Record<string, Array<{ t?: string; v?: number }>>
    }>,

  getTemperatureSensorReadings: ({
    sensorId,
    dateRange,
    resolution = '30s',
  }: {
    sensorId: number
    dateRange: string
    resolution?: string
  }) =>
    ApiService.get(
      `/v1/companies/${getCurrentCompanyId()}/sensors/temperature_sensors/${sensorId}/readings?${dateRange}&window_period=${encodeURIComponent(resolution)}`,
    ),
  getSensorTotalizer: ({ sensorId, dateRange, shiftId }: { sensorId: number; dateRange: string; shiftId: number | null }) =>
    ApiService.get(`/v1/companies/${getCurrentCompanyId()}/sensors/energy_sensors/${sensorId}/totalizer?${dateRange}&shift_id=${shiftId}`),
  activateDoorOpenAlarm: (sensorId: number, params: IDoorOpenAlarmParams) =>
    ApiService.post(`/v1/companies/${getCurrentCompanyId()}/sensors/open_sensors/${sensorId}/activate_door_open_alarm`, params),

  getDoorOpenPeriods: ({ sensorId, startDate, endDate }: { sensorId: number; startDate: string; endDate: string }) =>
    ApiService.get(`/v1/companies/${getCurrentCompanyId()}/sensors/open_sensors/${sensorId}/analyze_open_periods`, {
      start_date: startDate,
      end_date: endDate,
    }),

  updateTemperatureSensor: (sensorId: number, params: any) =>
    ApiService.put(`/v1/companies/${getCurrentCompanyId()}/sensors/temperature_sensors/${sensorId}/sensor_settings`, params),
  updateEnergySensor: (sensorId: number, params: any) =>
    ApiService.put(`/v1/companies/${getCurrentCompanyId()}/sensors/energy_sensors/${sensorId}/sensor_settings`, params),
  updateDoorOpenSensor: (sensorId: number, params: any) =>
    ApiService.put(`/v1/companies/${getCurrentCompanyId()}/sensors/open_sensors/${sensorId}/sensor_settings`, params),

  getMixingCycles: ({ sensorId, startDate, endDate }: IFetchMixingCycleParams) =>
    ApiService.get(`/v1/companies/${getCurrentCompanyId()}/sensors/energy_sensors/${sensorId}/mixing_cycles?start_date=${startDate}&end_date=${endDate}`),

  getMixingCycleSettings: (sensorId: number) =>
    ApiService.get(`/v1/companies/${getCurrentCompanyId()}/sensors/energy_sensors/${sensorId}/mixing_cycle_settings`),

  updateMixingCycleSettings: (sensorId: number, params: IMixingCycleSettings) =>
    ApiService.put(`/v1/companies/${getCurrentCompanyId()}/sensors/energy_sensors/${sensorId}/update_mixing_cycle_settings`, params),

  getSchemaSensors: (sensorType: string) =>
    ApiService.get(`/v1/companies/${getCurrentCompanyId()}/schema_sensors`, { sensor_type: sensorType }),

  assignToSensor: (schemaId: number, sensorId: number) =>
    ApiService.post(`/v1/companies/${getCurrentCompanyId()}/schema_sensors/${schemaId}/assign_to_sensor/${sensorId}`),

  assignSchemas: (body: {sensor_type: string, payload: any}) => ApiService.post(`/v1/companies/${getCurrentCompanyId()}/sensors/assign_schemas`, body),

  createSensor: (params: ICreateSensorParams) => ApiService.post(`/v1/companies/${getCurrentCompanyId()}/sensors/create_sensor`, params),

  /** Desativa o sensor: encerra alarmes abertos com a hora atual, zera as flags
   * de análise (active_*_alarm) no company_alarm_sensors e marca archived_at. */
  archiveSensor: (sensorId: number) =>
    ApiService.post(`/v1/companies/${getCurrentCompanyId()}/sensors/${sensorId}/archive`),

  /** Reativa o sensor (limpa archived_at). NÃO restaura as flags de análise —
   * elas precisam ser religadas manualmente nos endpoints `activate_*_alarm`. */
  unarchiveSensor: (sensorId: number) =>
    ApiService.post(`/v1/companies/${getCurrentCompanyId()}/sensors/${sensorId}/unarchive`),
}
export default SensorResource
